import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  selectTicketItems,
  selectTicketSort,
  setTicketSort,
} from "../../store/slices/ticketsSlice";
import { formatDateTime } from "../../utils/date";

const columns = [
  { id: "id", label: "Ticket ID", minWidth: 200 },
  { id: "barcode", label: "Barcode", minWidth: 160 },
  { id: "event", label: "Event", minWidth: 200 },
  { id: "last_tap_type", label: "Last Tap", width: 120 },
  { id: "last_station_code", label: "Station", width: 120 },
  { id: "last_tap_datetime", label: "Last Tap Time", minWidth: 180 },
  { id: "ticket_usage", label: "Usage", width: 100 },
  { id: "ticket_source", label: "Job Id", minWidth: 220 },
  { id: "uploader_id", label: "Uploader", minWidth: 160 },
  { id: "created_datetime", label: "Created", minWidth: 180 },
];

const TicketTable = ({ dense = false }) => {
  const dispatch = useDispatch();
  const rows = useSelector(selectTicketItems);
  const sort = useSelector(selectTicketSort);
  const theme = useTheme();
  const headerGradient =
    "linear-gradient(90deg, #030712 0%, #0f172a 50%, #111827 100%)";

  const sortableMap = {
    id: "id",
    barcode: "barcode",
    event: "event_id",
    ticket_usage: "ticket_usage",
    last_tap_type: null,
    last_tap_datetime: "last_tap_datetime",
    created_datetime: "created_datetime",
  };

  const handleSort = (columnId) => {
    const field = sortableMap[columnId];
    if (!field) return;
    const isSameField = sort.field === field;
    const nextDirection = isSameField && sort.direction === "asc" ? "desc" : "asc";
    dispatch(setTicketSort({ field, direction: nextDirection }));
  };

  const renderValue = (row, column) => {
    switch (column.id) {
      case "event":
        return row.event_name || row.event?.name || `#${row.event_id || "-"}`;
      case "last_tap_datetime":
      case "created_datetime":
        return formatDateTime(row[column.id]);
      case "ticket_usage":
        return row.ticket_usage ?? 0;
      case "last_tap_type":
        return row.last_tap_type || "-";
      case "ticket_source":
        return row.ticket_source ? `#${row.ticket_source}` : "-";
      default:
        return row[column.id] || "-";
    }
  };

  return (
    <TableContainer
      component={Box}
      sx={{ minWidth: "100%", width: "100%", overflowX: "auto" }}
    >
      <Table
        size={dense ? "small" : "medium"}
        sx={{ minWidth: 1200, borderCollapse: "separate", borderSpacing: 0 }}
      >
        <TableHead
          sx={{
            background: headerGradient,
            "& th": {
              borderBottom: "none",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.3,
              color: theme.palette.common.white,
              "& .MuiTableSortLabel-root": {
                color: "inherit",
                "&:hover": {
                  color: theme.palette.common.white,
                },
                "&.Mui-active": {
                  color: theme.palette.common.white,
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: `${theme.palette.common.white} !important`,
              },
            },
            "& th:first-of-type": {
              borderTopLeftRadius: 10,
            },
            "& th:last-of-type": {
              borderTopRightRadius: 10,
            },
          }}
        >
          <TableRow>
            {columns.map((column) => {
              const field = sortableMap[column.id];
              const isActive = field && sort.field === field;
              return (
                <TableCell key={column.id} sx={{ minWidth: column.minWidth }}>
                  {field ? (
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? sort.direction : "asc"}
                      onClick={() => handleSort(column.id)}
                      sx={{
                        color: "inherit",
                        "& .MuiTableSortLabel-icon": {
                          color: "inherit !important",
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.id}-${row.barcode}`}
              hover
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(15, 23, 42, 0.02)",
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={`${row.id}-${column.id}`}
                  sx={
                    column.id === "id"
                      ? { whiteSpace: "nowrap", fontFamily: "monospace" }
                      : undefined
                  }
                >
                  {renderValue(row, column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography
                  align="center"
                  color="text.secondary"
                  sx={{ py: 4 }}
                >
                  No tickets match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TicketTable;
